import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import categoryUseCaseV2 from '../../factories/v2/category';

// Query keys
export const REACT_QUERY_GET_ALL_CATEGORIES_V2_QUERY_KEY = 'getAllCategoriesV2';
export const REACT_QUERY_GET_CATEGORIES_V2_QUERY_KEY = 'getCategoriesV2';
export const REACT_QUERY_GET_CATEGORY_V2_QUERY_KEY = 'getCategoryV2';

/**
 * Get all categories (V2)
 */
export const useGetAllCategoriesV2 = (options = {}) => {
  const query = useQuery({
    queryKey: [REACT_QUERY_GET_ALL_CATEGORIES_V2_QUERY_KEY],
    queryFn: () => categoryUseCaseV2.getAllCategories(),
    ...options,
  });

  return query;
};

/**
 * Get categories with params (V2)
 */
export const useGetCategoriesV2 = (params, options = {}) => {
  const query = useQuery({
    queryKey: [REACT_QUERY_GET_CATEGORIES_V2_QUERY_KEY, params],
    queryFn: () => categoryUseCaseV2.getCategories(params),
    ...options,
  });

  return query;
};

/**
 * Get single category by ID (V2)
 */
export const useGetCategoryV2 = (id, options = {}) => {
  const query = useQuery({
    queryKey: [REACT_QUERY_GET_CATEGORY_V2_QUERY_KEY, id],
    queryFn: () => categoryUseCaseV2.getCategoryById(id),
    enabled: !!id,
    ...options,
  });

  return query;
};

/**
 * Create category (V2)
 */
export const useCreateCategoryV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: categoryUseCaseV2.createCategory,
    onSuccess: () => {
      // Invalidate all category queries
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_ALL_CATEGORIES_V2_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_CATEGORIES_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Update category (V2)
 */
export const useUpdateCategoryV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ categoryId, data }) => categoryUseCaseV2.updateCategory(categoryId, data),
    onSuccess: (category, variables) => {
      // Invalidate specific category and lists
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_GET_CATEGORY_V2_QUERY_KEY, variables.categoryId],
      });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_ALL_CATEGORIES_V2_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_CATEGORIES_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Delete category (V2)
 */
export const useDeleteCategoryV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: categoryUseCaseV2.deleteCategory,
    onSuccess: () => {
      // Invalidate all category queries
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_ALL_CATEGORIES_V2_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_CATEGORIES_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Reorder categories (V2)
 */
export const useReorderCategoriesV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data) => categoryUseCaseV2.reorderCategories(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_ALL_CATEGORIES_V2_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_CATEGORIES_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Upload category image
 */
export const useUploadCategoryImageV2 = () => {
  return useMutation({
    mutationFn: categoryUseCaseV2.uploadImage,
  });
};

/**
 * Upload category icon
 */
export const useUploadCategoryIconV2 = () => {
  return useMutation({
    mutationFn: categoryUseCaseV2.uploadIcon,
  });
};
